// BUILD-042 — Unified employee workspace. Compact employee identity card (photo/initials +
// name/title/department/status/date) shown above the grouped section nav, per the approved
// decision's visual contract. Reuses the existing avatar-click -> ChangeProfilePictureModal
// interaction already built for My HR (MyHrNav.tsx) — carried into this shared component rather
// than duplicated, so the picture-change affordance is identical in both My HR and HR
// Administration's employee view.
import { useState } from 'react';
import { ChangeProfilePictureModal } from '../ChangeProfilePictureModal';
import { initialsFrom } from './Avatar';
import { formatLocalDate } from '../hrFormat';
import { useDepartmentNames } from '../useDepartmentNames';

export interface EmployeeCardInfo {
  name: string;
  title?: string | null;
  departmentId?: string | null;
  employmentStatus?: string | null;
  hireDate?: string | null;
  /** Only the SELF card gets the photo-change affordance — an HR Administrator viewing someone
   *  else's record never gets a control that implies they can change that Employee's photo on
   *  their behalf (no such capability/backend exists). */
  allowPhotoChange?: boolean;
}

export function EmployeeCard({ info }: { info: EmployeeCardInfo }) {
  const [photo, setPhoto] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const departmentName = useDepartmentNames().nameOf(info.departmentId);
  const statusLabel = info.employmentStatus
    ? info.employmentStatus.charAt(0).toUpperCase() + info.employmentStatus.slice(1).replace(/_/g, ' ')
    : null;
  // Cycle 18: initials come from the name WITHOUT a parenthetical (a nickname or a "(synthetic)" tag), so "Riley Report (synthetic)" reads "RR", not "R(".
  // The admin rail's old identity block did this; it was lost when the admin view moved to this shared card (found by inspecting the browser screenshot).
  const initials = initialsFrom(info.name.replace(/\s*\([^)]*\)\s*/g, ' ').trim() || info.name);

  // Owner-directed (2026-10-07): the photo/initials circle is 50% larger (56px -> 84px) ONLY on
  // My HR's own card. `allowPhotoChange` already distinguishes exactly that case (true only for
  // the Employee's own card — see this interface's own doc comment above), so it doubles as the
  // "is this My HR" signal here rather than adding a second prop; HR Administration's employee
  // card (allowPhotoChange=false) keeps the original 56px size, unchanged.
  const avatarSize = info.allowPhotoChange ? 'w-[84px] h-[84px]' : 'w-14 h-14';
  const avatarTextSize = info.allowPhotoChange ? 'text-xl' : 'text-base';
  const avatarIconSize = info.allowPhotoChange ? 'w-6 h-6' : 'w-4 h-4';

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 text-center">
      {info.allowPhotoChange ? (
        <button
          type="button"
          onClick={() => setPickerOpen(true)}
          className={`group relative ${avatarSize} rounded-full mx-auto mb-2.5`}
          aria-label={photo ? `Change profile picture for ${info.name}` : `Add a profile picture for ${info.name} — currently showing initials`}
        >
          {photo ? (
            <img src={photo} alt="" className={`${avatarSize} rounded-full object-cover`} />
          ) : (
            <div className={`${avatarSize} rounded-full flex items-center justify-center text-white ${avatarTextSize} font-semibold bg-[#4AABDE] mx-auto`}>
              {initials}
            </div>
          )}
          <span className="absolute inset-0 rounded-full bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
            <svg className={`${avatarIconSize} text-white opacity-0 group-hover:opacity-100 transition-opacity`} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909M3 20.25h18A1.5 1.5 0 0 0 22.5 18.75V5.25A1.5 1.5 0 0 0 21 3.75H3a1.5 1.5 0 0 0-1.5 1.5v13.5A1.5 1.5 0 0 0 3 20.25Zm13.5-12.75a1.125 1.125 0 1 1-2.25 0 1.125 1.125 0 0 1 2.25 0Z" />
            </svg>
          </span>
        </button>
      ) : (
        <div className={`${avatarSize} rounded-full flex items-center justify-center text-white ${avatarTextSize} font-semibold bg-[#4AABDE] mx-auto mb-2.5`}>
          {initials}
        </div>
      )}
      {pickerOpen && (
        <ChangeProfilePictureModal currentPhoto={photo} onSave={setPhoto} onClose={() => setPickerOpen(false)} />
      )}
      <div className="text-base font-bold text-gray-900">{info.name}</div>
      {/* The department is shown by NAME (it used to print the stored id). A department that was disabled later still shows its name. */}
      {(info.title || departmentName) && (
        <div className="text-xs text-gray-500 mt-1" data-testid="employee-card-title-department">
          {info.title ?? '—'}{departmentName ? ` · ${departmentName}` : ''}
        </div>
      )}
      {(statusLabel || info.hireDate) && (
        <div className="mt-2.5">
          <span className="inline-flex items-center text-xs font-medium rounded-full px-2.5 py-1 bg-[#EDF5FC] text-[#176A9B]">
            {/* October 10 22:28 addendum: hire date previously rendered as the raw stored
                YYYY-MM-DD string with no formatting at all — now MM/DD/YYYY via the same
                date-only-safe formatter used elsewhere in HR. Display only; stored value
                unchanged. */}
            {statusLabel ?? 'Status unknown'}{info.hireDate ? ` · Hired ${formatLocalDate(info.hireDate)}` : ''}
          </span>
        </div>
      )}
    </div>
  );
}
